'use client';
import { useEffect, useState, useRef, useCallback, useContext } from 'react';

import {
  useApolloClient,
  useReactiveVar,
  useSuspenseQuery,
} from '@apollo/client';
import { Button } from '@veneer/core';
import { usePathname, useSearchParams } from 'next/navigation';
import { useErrorHandler } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { FlowOrchestratorContext } from '@storefront/app/flow-orchestrator/context';
import ThemeProviderWrapperV2 from '@storefront/app/ThemeProviderWrapperV2';
import { logAnalytics } from '@storefront/app/v2/pdp-config/printer/continue-button/continue-button-utils';
import { configurationState } from '@storefront/app/v2/pdp-config/printer/state-management/pdp-config-state';
import {
  getSkuList,
  saveProductConfiguration,
} from '@storefront/app/v2/pdp-config/printer/utils/utils';
import {
  BundleProduct,
  GetPdpStateDocument,
  GetPdpStateQuery,
  GetSkuProductsDocument,
  GetSkuProductsQuery,
} from '@storefront/data-commerce';
import { useIsUserOnline } from '@storefront/hooks/useIsUserOnline';
import useMutateAddProductsCart from '@storefront/hooks/useMutateAddProductsCart';
import useUdlStore from '@storefront/hooks/useUdl';
import { NamespacesI18n } from '@storefront/i18n';
import AttentiveAnalyticsHandler from '@storefront/lib/AttentiveAnalytics';
import { getLogger } from '@storefront/logging';
import DataCollection from '@storefront/services/DataCollection';
import { uiEvents } from '@storefront/services/DataCollection/simpleUiEvents';
import { saveCurrentProductConfigurationURL } from '@storefront/services/SessionHelperService/sessionHelperService';
import udlEvents from '@storefront/services/udl/udlEvents';
import { HeldError } from '@storefront/shared-components/error-information/held-error';
import { useCreateEmptyCartMutation } from '@storefront/utils/checkout.mutation.utils';

import styles from './continue-button.module.scss';

interface ContinueButtonProps {
  isDisabled: boolean;
  loading: boolean;
  isLoading: (loading: boolean) => void;
  hasTriggeredEvent: boolean;
  setHasTriggeredEvent: (value: boolean) => void;
}

const logger = getLogger('continue-button');

export const ContinueButton = ({
  isDisabled,
  loading,
  isLoading,
  hasTriggeredEvent,
  setHasTriggeredEvent,
}: ContinueButtonProps) => {
  const searchParams = useSearchParams();
  const { mutateEmptyCart } = useCreateEmptyCartMutation();
  const configuration = useReactiveVar(configurationState);
  const udlStore = useUdlStore();
  const skuList = getSkuList(searchParams?.get('skus'));
  const { t } = useTranslation(NamespacesI18n.PDP_CONFIG);
  const [tracingId] = useState(`pdp-config-btn-${uuidv4()}`);
  const setError = useErrorHandler();
  const isUserOnline = useIsUserOnline();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { goToNextStep } = useContext(FlowOrchestratorContext);

  const client = useApolloClient();

  const {
    pdpConfig: { selected_options },
  } = client.readQuery<GetPdpStateQuery>({
    query: GetPdpStateDocument,
    variables: { skuList, configuration },
  });

  const {
    data: { products },
  } = useSuspenseQuery<GetSkuProductsQuery>(GetSkuProductsDocument, {
    variables: { skuList },
  });
  const cartItems = [
    {
      sku: configuration.activeSku as string,
      selected_options,
      quantity: 1,
    },
  ];

  const { addProductsToCart } = useMutateAddProductsCart(cartItems);
  const pathName = usePathname();

  const handleIntersection = useCallback(
    async (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && !hasTriggeredEvent && !isDisabled) {
        await DataCollection.sendEvent(
          uiEvents.reviewCartControlDisplayedPrinter
        );
        udlStore.trackEvent(udlEvents.getEventByID('checkoutButtonDisplayed'));
        setHasTriggeredEvent(true);
      }
    },
    [isDisabled, udlStore, hasTriggeredEvent, setHasTriggeredEvent]
  );

  const attentive = AttentiveAnalyticsHandler.getInstance();

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.1,
    });
    if (buttonRef.current) {
      observer.observe(buttonRef.current);
    }
    return () => {
      if (buttonRef.current) {
        observer.unobserve(buttonRef.current);
      }
    };
  }, [handleIntersection]);

  const handleClick = async () => {
    try {
      isLoading(true);
      const {
        data: { createEmptyCart: cartId },
      } = await mutateEmptyCart();
      saveProductConfiguration(configuration);
      saveCurrentProductConfigurationURL(
        `${pathName}?${searchParams?.toString()}`
      );
      attentive.setAddProductsToCart(cartId, {
        activeSKU: configuration.activeSku as string,
        products: products,
        selectedOptions: selected_options,
      });
      await addProductsToCart(cartId);
      await logAnalytics(
        configuration,
        products.items as BundleProduct[],
        udlStore,
        tracingId
      );
      goToNextStep({
        params: { id: cartId },
      });
    } catch (error) {
      isLoading(false);
      let message = '';
      let i18nPath = '';
      if (error instanceof HeldError) i18nPath = error.i18nPath;

      const errorCode = i18nPath || 'errors.SOMETHING_WENT_WRONG';
      setError(
        new HeldError(isUserOnline ? errorCode : 'errors.NETWORK_ERROR')
      );
      if (error instanceof Error) message = error.message;
      logger.error({
        msg: 'Error on handling cart creation',
        error: message,
      });
    }
  };

  return (
    <ThemeProviderWrapperV2 shape="round">
      <Button
        className={`label-regular ${styles['continue-button']}`}
        id="pdp-continue-button"
        data-testid="pdp-continue-button"
        onClick={handleClick}
        disabled={isDisabled}
        loading={loading}
        ref={buttonRef}
      >
        <span className={`label-regular`}>{t('pdpConfig.continue')}</span>
      </Button>
    </ThemeProviderWrapperV2>
  );
};
